<template>
  <router-view />
  <metainfo>
    <template #title="{ metainfo }">
      {{ titleTemplate(metainfo.title) }}
    </template>
  </metainfo>
  <noty-container />
  <fresh-chat v-if="isFreshChatEnabled" />
</template>

<script setup>
import { useSettings } from '@/store/settings'
import { useFeatures } from '@/store/features'
import { useMeta } from 'vue-meta'
import { useSockets } from '@/libs/composables/sockets'
import { useWallets } from '@/store/wallets'
import { useReports } from '@/store/reports'
import { storeToRefs } from 'pinia'
import { beneficiary, wallet } from '@/libs/api'
import NotyContainer from '@/libs/noty/NotyContainer.vue'
import { createNoty, NOTIFICATION_TYPE_ERROR } from '@/libs/noty'
import { useNoty } from '@/libs/composables/useNoty'
import { useNotifications } from '@/store/notifications'
import { useModals } from '@/store/modals.js'
import { watch } from 'vue'
import FreshChat from '@/libs/freshchat/FreshChat.vue'
import { useRoute, useRouter } from 'vue-router'
import { useAuth } from '@/store/auth.js'
import { isEqualNumber, openOncePerEvent } from '@/libs/functions.js'
import {
  isMyOperation,
  hasRedirectPerformed,
  setRedirectPerformed,
} from '@/libs/composables/openBankingSession.js'
import { useOpenBanking } from '@/store/openBanking.js'
import { useBeneficiaries } from '@/store/beneficiaries.js'
import { useAuthSessions } from '@/store/authSessions.js'
import { SessionDecorator } from '@/components/settings/security/decorators/SessionDecorator.js'
import { useOob } from '@/store/oob.js'
import { useUser } from '@/store/user.js'

const isFreshChatEnabled = !import.meta.env.DEV

const router = useRouter()
const route = useRoute()
const { noty } = useNoty()

const OPEN_BANKING_STATUS_ROUTES = new Set([
  'OpenBankingReturn',
  'OpenBankingExpiry',
  'OpenBankingCancel',
])

function isOnOpenBankingStatusScreen() {
  return OPEN_BANKING_STATUS_ROUTES.has(route.name)
}

function notifyOpenBankingOutcomeIfNeeded(payload, messageKey) {
  if (!isMyOperation(payload.walletId, payload.id)) return
  if (isOnOpenBankingStatusScreen()) return
  noty(messageKey, { type: NOTIFICATION_TYPE_ERROR })
}

const reports = useReports()
const { settings } = useSettings()
const { hasActiveModal } = storeToRefs(useModals())

/**
 * Prevent scroll for body tag if modal opened
 */
watch(hasActiveModal, (n) => {
  let modalClass = 'scroll-prevent'
  let body = document.querySelector('body')
  let method = n ? 'add' : 'remove'

  body.classList[method](modalClass)
})

/**
 * Render title format as {component_name} - {brand_name}
 * @param pageName
 * @returns {string}
 */
const titleTemplate = (pageName) => {
  return pageName && pageName !== settings.title
    ? pageName + ' - ' + settings.title
    : settings.title
}

const scripts = []
const links = []

if (settings.js_url) {
  scripts.push({
    src: settings.js_url,
    async: true,
  })
}

if (settings.css_url) {
  links.push({
    rel: 'stylesheet',
    href: settings.css_url,
  })
}

if (settings.favicon_url) {
  links.push({
    rel: 'icon',
    href: settings.favicon_url,
  })
}

useMeta({
  title: settings.title,
  description: settings.description,
  link: links,
  script: scripts,
})

const { setWallet } = useWallets()
const { getWalletById } = storeToRefs(useWallets())
const { beneficiaries } = storeToRefs(useBeneficiaries())
const { sessions } = storeToRefs(useAuthSessions())
const { getNotificationsInfo, resetUnreadNotifications } = useNotifications()

const oob = useOob()

useSockets({
  'v1.wallet.topUpFromCardStart': ({ redirectUrl, id }) => {
    openOncePerEvent(id, redirectUrl)
  },
  'v1.wallet.topUpFromCardError': ({ error }) => {
    createNoty(error, { type: NOTIFICATION_TYPE_ERROR })
  },
  'v1.wallet.topUpFromOpenBankingStart': (payload) => {
    const { id: operationId, walletId, redirectUrl } = payload

    // Ignore events from other tabs or other operations
    if (!isMyOperation(walletId, operationId)) {
      return
    }

    // Prevent repeated navigation (duplicate event or post-return replay)
    if (hasRedirectPerformed(operationId)) {
      return
    }

    // Validate redirect URL before navigating
    try {
      const parsed = new URL(redirectUrl)
      if (parsed.protocol !== 'http:' && parsed.protocol !== 'https:') {
        return
      }
    } catch {
      return
    }

    useOpenBanking().applyWsPayload(payload)
    setRedirectPerformed(operationId)
    window.location.assign(redirectUrl)
  },
  'v1.wallet.topUpFromOpenBankingPendingCredit': (payload) => {
    useOpenBanking().applyWsPayload(payload)
  },
  'v1.wallet.topUpFromOpenBankingSuccess': async (payload) => {
    useOpenBanking().applyWsPayload(payload)

    // Show success notification; the return modal remains in pending state
    noty('wallet.topUp.openBankingCreditSuccess', {
      type: 'success',
    })

    // Refresh wallet balance; v3.wallet.update may also arrive independently
    const { data } = await wallet.getDetails(payload.walletId)
    if (data) {
      setWallet(data)
    }
  },
  'v1.wallet.topUpFromOpenBankingError': (payload) => {
    useOpenBanking().applyWsPayload(payload)
    notifyOpenBankingOutcomeIfNeeded(
      payload,
      'wallet.topUp.openBankingReturnError',
    )
  },
  'v1.wallet.topUpFromOpenBankingExpiry': (payload) => {
    useOpenBanking().applyWsPayload(payload)
    notifyOpenBankingOutcomeIfNeeded(
      payload,
      'wallet.topUp.openBankingReturnExpired',
    )
  },
  'v3.wallet.update': async ({ uuid }) => {
    const { data } = await wallet.getDetails(uuid)
    if (data) {
      setWallet(data)
    }
  },
  createBeneficiary: async ({ id }) => {
    const data = beneficiaries.value.find((i) => isEqualNumber(i.id, id))
    if (!data) {
      const { data: item } = await beneficiary.getBeneficiary(id)
      beneficiaries.value.unshift(item)
    }
  },
  updateBeneficiary: async ({ id }) => {
    const index = beneficiaries.value.findIndex((i) => isEqualNumber(i.id, id))
    if (index > -1) {
      const { data: item } = await beneficiary.getBeneficiary(id)
      beneficiaries.value[index] = item
    }
  },
  'v1.beneficiary.update': (payload) => {
    useBeneficiaries().applyUpdate(payload)
  },
  'signal.sortOrderBeneficiary.error': ({ error }) => {
    if (error) {
      createNoty(error, { type: NOTIFICATION_TYPE_ERROR })
    }
  },
  deleteBeneficiary: async ({ id }) => {
    const index = beneficiaries.value.findIndex((i) => i.id === id)
    if (index > -1) {
      beneficiaries.value.splice(index, 1)
    }
  },
  'v3.wallet.create': async ({ uuid }) => {
    const { data } = await wallet.getDetails(uuid)
    if (data) {
      setWallet(data)
    }
  },
  'v2.walletReport.create'(data) {
    reports.addReport(data)
  },
  'wallet.report'({ status, id, url }) {
    if (status === 'SUCCESS') {
      reports.updateReportById(id, { status: 'DONE', url })
      noty('notifications.reportReady', {
        type: 'success',
        closable: true,
      })
    } else if (status === 'ERROR') {
      reports.deleteReportById(id)
      noty('notifications.reportGenerateError', {
        type: 'warning',
        closable: true,
      })
    }
  },
  'v2.walletReport.delete'({ id }) {
    reports.deleteReportById(id)

    if (route.path.includes(id)) {
      router.push({ name: 'Reports' })
    }
  },
  unreadNotification({ status }) {
    status ? getNotificationsInfo() : resetUnreadNotifications()
  },
  'v3.auth.logout'() {
    setTimeout(() => {
      router.push({ name: 'Logout' })
    }, 1000)
  },
  refreshAccessToken() {
    useAuth().refresh()
  },
  'v2.auth.create': (session) => {
    if (!sessions.value.some((i) => i.id === session.id)) {
      sessions.value.unshift(SessionDecorator(session))
    }
  },
  'v2.auth.delete': ({ id }) => {
    sessions.value = sessions.value.filter((i) => i.id !== id)
  },
  'signal.features.update': async () => {
    await useUser().getUser()
    await useFeatures().getFeatures()
  },
  'v3.wallet.updateCardsCount': async ({ id, cardCount }) => {
    const wallet = getWalletById.value(id)
    if (wallet) {
      wallet.data.count = cardCount
    }
  },
  'v1.cardOob.initiate': (data) => {
    oob.addRequest(data)
  },
  'v1.cardOob.response': (data) => {
    oob.removeRequest(data.requestId)
  },
})
</script>

<style lang="scss" src="./assets/styles/main.scss" />
